import { useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Upload, Trash2, Eye, EyeOff, Pencil } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

export default function GalleryManager() {
  const [classFilter, setClassFilter] = useState("");
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null);
  const fileInputRef = useRef(null);

  const { data: photos = [], refetch, isFetching } = useQuery({
    queryKey: ["adminGallery", classFilter],
    queryFn: async () => {
      const q = classFilter ? `?class_id=${classFilter}` : "";
      return (await api.get(`/admin/gallery/photos${q}`)).data;
    },
    refetchInterval: 30_000,
  });

  const { data: classes = [] } = useQuery({
    queryKey: ["adminClassesForGallery"],
    // /admin/classes returns past + upcoming classes (sorted by date). For gallery
    // tagging, the most useful options are PAST classes — those that already happened.
    queryFn: async () => (await api.get("/admin/classes?include_archived=true")).data,
  });

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let okCount = 0;
    let failCount = 0;
    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("caption", "");
      formData.append("alt_text", file.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " "));
      if (classFilter) formData.append("class_id", classFilter);
      try {
        await api.post("/admin/gallery/photos", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        okCount++;
      } catch (e) {
        failCount++;
        console.error("Upload failed", file.name, e?.response?.data);
      }
    }
    setUploading(false);
    if (okCount) toast.success(`Uploaded ${okCount} photo${okCount > 1 ? "s" : ""}`);
    if (failCount) toast.error(`${failCount} upload${failCount > 1 ? "s" : ""} failed`);
    refetch();
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const togglePublished = async (p) => {
    try {
      await api.patch(`/admin/gallery/photos/${p.id}`, { published: !p.published });
      refetch();
    } catch (e) {
      toast.error("Could not toggle");
    }
  };

  const deletePhoto = async (p) => {
    if (!window.confirm(`Delete this photo? This soft-deletes it from the gallery (the file stays in storage).`)) return;
    try {
      await api.delete(`/admin/gallery/photos/${p.id}`);
      toast.success("Photo deleted");
      refetch();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-gg-border bg-gg-surface p-5 sm:p-6">
        <h3 className="font-heading text-xl text-[var(--gg-text)] mb-3">Upload photos</h3>
        <p className="text-sm text-gg-muted mb-4">
          JPEG, PNG, WebP, or GIF — up to 10 MB each. After upload, click any photo to edit caption, alt-text (for SEO), tags, and class tag.
        </p>
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[200px]">
            <Label htmlFor="gallery-class-filter" className="text-xs uppercase tracking-[0.14em] text-gg-brown font-semibold">
              Tag uploads to class (optional)
            </Label>
            <select
              id="gallery-class-filter"
              value={classFilter}
              onChange={(e) => setClassFilter(e.target.value)}
              className="mt-1 w-full border border-gg-border rounded-md px-3 py-2 text-sm bg-white"
              data-testid="gallery-class-filter-select"
            >
              <option value="">— No class tag —</option>
              {classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title} ({c.class_date})
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="btn-pill btn-primary disabled:opacity-40"
            data-testid="gallery-upload-btn"
          >
            <Upload size={14} /> {uploading ? "Uploading…" : "Choose photos"}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={(e) => handleFiles(Array.from(e.target.files || []))}
            className="hidden"
            data-testid="gallery-upload-input"
          />
        </div>
      </div>

      {photos.length === 0 ? (
        <div className="rounded-2xl border border-gg-border bg-gg-surface p-16 text-center text-gg-muted">
          {classFilter ? "No photos tagged to this class yet." : "No photos uploaded yet. Drop some above to get started!"}
        </div>
      ) : (
        <div
          className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
          data-testid="gallery-admin-grid"
        >
          {photos.map((p) => (
            <PhotoCard
              key={p.id}
              photo={p}
              onEdit={() => setEditing(p)}
              onTogglePublished={() => togglePublished(p)}
              onDelete={() => deletePhoto(p)}
            />
          ))}
        </div>
      )}

      {editing && (
        <PhotoEditDialog
          photo={editing}
          classes={classes}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refetch(); }}
        />
      )}
    </div>
  );
}

function PhotoCard({ photo, onEdit, onTogglePublished, onDelete }) {
  return (
    <div
      className="rounded-2xl border border-gg-border bg-gg-surface overflow-hidden group"
      data-testid={`gallery-admin-card-${photo.id}`}
    >
      <div className="relative aspect-square bg-gg-bg">
        <img
          src={photo.image_url}
          alt={photo.alt_text || photo.caption || ""}
          loading="lazy"
          className="w-full h-full object-cover"
        />
        {!photo.published && (
          <span className="absolute top-2 left-2 text-[10px] uppercase tracking-[0.14em] bg-black/70 text-white px-2 py-0.5 rounded-full">
            Hidden
          </span>
        )}
      </div>
      <div className="p-3 space-y-2">
        {photo.class_title && (
          <p className="text-[10px] uppercase tracking-[0.14em] text-gg-brown font-semibold truncate">
            {photo.class_title}
          </p>
        )}
        <p className="text-sm text-[var(--gg-text)] line-clamp-2 min-h-[2.5rem]">
          {photo.caption || <span className="text-gg-muted italic">No caption</span>}
        </p>
        <div className="text-[10px] text-gg-muted space-y-0.5">
          {photo.alt_text && <p className="line-clamp-1">alt: {photo.alt_text}</p>}
          {photo.tags?.length > 0 && <p className="line-clamp-1">tags: {photo.tags.join(", ")}</p>}
          <p>sort: {photo.sort_order}</p>
        </div>
        <div className="flex items-center justify-between gap-1 pt-1">
          <button
            type="button"
            onClick={onEdit}
            className="text-xs px-2 py-1 rounded-full bg-gg-bg border border-gg-border text-gg-brown hover:border-gg-green hover:text-gg-green inline-flex items-center gap-1"
            data-testid={`gallery-edit-${photo.id}`}
          >
            <Pencil size={11} /> Edit
          </button>
          <button
            type="button"
            onClick={onTogglePublished}
            className="text-xs px-2 py-1 rounded-full bg-gg-bg border border-gg-border text-gg-brown hover:border-gg-green hover:text-gg-green inline-flex items-center gap-1"
            data-testid={`gallery-publish-toggle-${photo.id}`}
            title={photo.published ? "Hide from public" : "Publish to public"}
          >
            {photo.published ? <Eye size={11} /> : <EyeOff size={11} />}
            {photo.published ? "Published" : "Hidden"}
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="text-xs px-2 py-1 rounded-full bg-gg-bg border border-gg-border text-gg-muted hover:border-[#B24C4C] hover:text-[#B24C4C] inline-flex items-center gap-1"
            data-testid={`gallery-delete-${photo.id}`}
          >
            <Trash2 size={11} />
          </button>
        </div>
      </div>
    </div>
  );
}

function PhotoEditDialog({ photo, classes, onClose, onSaved }) {
  const [form, setForm] = useState({
    caption: photo.caption || "",
    alt_text: photo.alt_text || "",
    class_id: photo.class_id || "",
    tags: (photo.tags || []).join(", "),
    seo_title: photo.seo_title || "",
    seo_description: photo.seo_description || "",
    sort_order: photo.sort_order || 0,
    published: photo.published !== false,
  });
  const [saving, setSaving] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        caption: form.caption,
        alt_text: form.alt_text,
        class_id: form.class_id || null,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        seo_title: form.seo_title || null,
        seo_description: form.seo_description || null,
        sort_order: Number(form.sort_order || 0),
        published: !!form.published,
      };
      await api.patch(`/admin/gallery/photos/${photo.id}`, payload);
      toast.success("Photo updated");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="gallery-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit photo</DialogTitle>
        </DialogHeader>
        <div className="grid sm:grid-cols-[1fr_2fr] gap-5 pt-2">
          <img
            src={photo.image_url}
            alt={photo.alt_text || photo.caption || ""}
            className="rounded-xl border border-gg-border w-full aspect-square object-cover"
          />
          <div className="space-y-3">
            <FieldLabel label="Caption (shown on hover & lightbox)">
              <Textarea
                rows={2}
                value={form.caption}
                onChange={(e) => update("caption", e.target.value)}
                data-testid="gallery-edit-caption"
              />
            </FieldLabel>
            <FieldLabel label="Alt text (SEO + screen readers — describe the image factually)">
              <Input
                value={form.alt_text}
                onChange={(e) => update("alt_text", e.target.value)}
                placeholder="Children harvesting carrots in a raised garden bed"
                data-testid="gallery-edit-alt"
              />
            </FieldLabel>
            <FieldLabel label="Tag to class">
              <select
                value={form.class_id}
                onChange={(e) => update("class_id", e.target.value)}
                className="w-full border border-gg-border rounded-md px-3 py-2 text-sm bg-white"
                data-testid="gallery-edit-class"
              >
                <option value="">— No class —</option>
                {classes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title} ({c.class_date})
                  </option>
                ))}
              </select>
            </FieldLabel>
            <FieldLabel label="Tags (comma-separated)">
              <Input
                value={form.tags}
                onChange={(e) => update("tags", e.target.value)}
                placeholder="spring, harvest, students"
                data-testid="gallery-edit-tags"
              />
            </FieldLabel>
            <div className="grid grid-cols-2 gap-3">
              <FieldLabel label="SEO title">
                <Input
                  value={form.seo_title}
                  onChange={(e) => update("seo_title", e.target.value)}
                  placeholder="Spring Harvest Class"
                  data-testid="gallery-edit-seo-title"
                />
              </FieldLabel>
              <FieldLabel label="Sort order (higher = first)">
                <Input
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => update("sort_order", e.target.value)}
                  data-testid="gallery-edit-sort"
                />
              </FieldLabel>
            </div>
            <FieldLabel label="SEO description">
              <Textarea
                rows={2}
                value={form.seo_description}
                onChange={(e) => update("seo_description", e.target.value)}
                placeholder="Eight kids harvesting carrots at our March homestead class"
                data-testid="gallery-edit-seo-desc"
              />
            </FieldLabel>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.published}
                onChange={(e) => update("published", e.target.checked)}
                data-testid="gallery-edit-published"
              />
              <span>Published (visible to the public)</span>
            </label>
          </div>
        </div>
        <DialogFooter className="mt-4">
          <button type="button" onClick={onClose} className="btn-pill btn-outline" disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            className="btn-pill btn-primary"
            disabled={saving}
            data-testid="gallery-edit-save"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldLabel({ label, children }) {
  return (
    <label className="block text-sm space-y-1">
      <span className="text-xs uppercase tracking-[0.14em] text-gg-brown font-semibold">{label}</span>
      {children}
    </label>
  );
}
